/* The gallery and the upload queue.
   ─────────────────────────────────────────────────────────────────────────────
   NOTE FOR WHOEVER PICKS THIS UP: the second seam. Everything a real
   `services/media` would own is behind this module — listing, cursor
   pagination, presigned PUTs, the processor's thumbnail callback. What runs here
   instead is a local store: fixtures for other guests, real File objects for
   yours, and a simulated transfer so the queue's honesty (progress, estimate,
   retry, cancel) can be built and judged. Blobs live in memory, so your own
   uploads do not survive a reload; nothing else about the model changes when the
   service arrives.

   Rules that are real and must stay real:
   · Every guest sees every upload (D35); you may edit and delete only your own,
     and ownership is the verified identity, not the browser (D44).
   · Video is capped at three minutes and checked BEFORE the transfer starts
     (D25) — never make a guest wait through an upload to be rejected at the end.
   · Videos never autoplay in the grid and are excluded from the slideshow (D26).
   · Admins can hide and unhide, reversibly, and cannot delete (D40). */

import { useEffect, useState } from 'react';
import { THUMB_LONG_EDGE_PX, type PhotosResponse } from '@mv/contracts';
import { ApiError, NotSignedIn, api } from './api';
import { needsMultipart, uploadMultipart, uploadSingle } from './transfer';
import { now } from './time';
import { getSession, type Suit } from './session';

export const MAX_VIDEO_SECONDS = 180;

export type MediaKind = 'photo' | 'video';
export type UploadState = 'uploading' | 'processing' | 'done' | 'failed' | 'rejected' | 'queued';

export interface MediaItem {
  id: string;
  kind: MediaKind;
  /** The display copy — 2560px, made in the browser. Never the camera original
   *  (D25 amendment). For fixtures this is a generated stand-in. */
  src: string;
  /** Grid thumbnail — the dominant bandwidth cost on the whole system.
   *  Empty string when a video's poster frame could not be grabbed (a codec
   *  the browser will not decode); the tile renders a blank frame with the
   *  play badge rather than a broken image. */
  thumb: string;
  durationSec?: number;
  uploaderId: string;
  uploaderName: string;
  uploaderTeam: Suit;
  caption?: string;
  eventId?: string;
  createdAt: number;
  hidden: boolean;
  state: UploadState;
  /** 0–1 while uploading. */
  progress?: number;
  /** Bytes per second, for the estimate. */
  fileName?: string;
  error?: string;
}

/* The gallery starts empty. It used to be seeded with eighteen invented
   uploads drawn as gradients, which was useful while the grid, the crossfade
   and the caption band were being built and is actively misleading now: the
   whole point of the next pass is watching real files go through the presign,
   the processor and the slideshow. Fake rows would sit in the middle of that
   looking exactly like successes. The empty states already exist for this
   (Gallery's EmptyFor, the kiosk hold frame) — they are now the first thing
   you see, which is correct. */

let items: MediaItem[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((fn) => fn());

let loaded = false;
let loading: Promise<void> | null = null;

/**
 * Pull the gallery from the API.
 *
 * **Three reads, not one.** `/photos` with no parameters is the public gallery,
 * and the API answers it from the `hidden=false` partition of GSI2 — so a
 * hidden photo is not filtered out of that response, it was never in it. The
 * client used to make this one call and then slice the result three ways for
 * the All / Mine / Hidden tabs, which meant Hidden could only ever be empty and
 * Mine silently dropped the caller's own hidden uploads. Hiding a photo made it
 * vanish from every view including the one built to bring it back.
 *
 * So each tab gets the read that actually answers it:
 *
 *   `/photos`              the gallery, hidden excluded (everyone)
 *   `/photos?mine=true`    the caller's own, hidden included (everyone)
 *   `/photos?hidden=true`  the hidden shelf (admin only, enforced server-side)
 *
 * The admin read is skipped for a guest rather than attempted and swallowed: it
 * would 403, and a request whose failure is routine is a request not worth
 * making.
 *
 * Server rows replace server rows; anything still in flight from this browser
 * is kept, because it does not exist on the server yet and dropping it would
 * make a guest's own upload vanish from under them mid-transfer.
 */
export async function refreshMedia(): Promise<void> {
  // Read straight from the session rather than threading a viewer through the
  // seven call sites — polling, retries and the post-upload refresh all want
  // the same answer, and `session.ts` does not import this module, so there is
  // no cycle to worry about.
  const viewer = getSession();

  const reads: Promise<PhotosResponse>[] = [
    api<PhotosResponse>('/photos?limit=100'),
    api<PhotosResponse>('/photos?mine=true&limit=100'),
  ];
  if (viewer?.isAdmin) reads.push(api<PhotosResponse>('/photos?hidden=true&limit=100'));

  const pages = await Promise.all(reads);

  const inFlight = items.filter((it) => it.state !== 'done');
  const seen = new Set(inFlight.map((it) => it.id));

  // Merged by id and first-wins. The lists genuinely overlap — the caller's own
  // visible photos are in both the gallery and `mine` — and a duplicate here is
  // a duplicate tile with a duplicate React key.
  const merged: MediaItem[] = [];
  for (const page of pages) {
    for (const row of page.items) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      merged.push({ ...row, state: 'done' });
    }
  }

  items = [...inFlight, ...merged];
  loaded = true;
  emit();
}

export function useMedia(): MediaItem[] {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    if (!loaded && !loading) {
      loading = refreshMedia()
        .catch(() => { /* Offline or signed out. The empty state says so. */ })
        .finally(() => { loading = null; });
    }
    return () => { listeners.delete(fn); };
  }, []);
  return items;
}

/** True once the first load has come back, so the gallery can tell "empty"
 *  apart from "not asked yet" and show a skeleton rather than "no photos". */
export const mediaLoaded = () => loaded;

/**
 * Poll for what other people have uploaded.
 *
 * The gallery and the projector both need photos that arrived from somebody
 * else's phone, and nothing pushes them here — there is no socket, by design
 * (D12/D17: this whole system is static files and stateless reads). A poll is
 * the honest shape for that.
 *
 * Skipped while the tab is hidden, so a phone in a pocket is not doing this all
 * night, and it fires once immediately on becoming visible again — a guest
 * coming back to the app should not wait an interval to see what they missed.
 */
export function useMediaPolling(everyMs: number): void {
  useEffect(() => {
    let cancelled = false;

    const pull = () => {
      if (cancelled || document.hidden) return;
      void refreshMedia().catch(() => { /* Offline. The last copy stays up. */ });
    };

    const id = setInterval(pull, everyMs);
    const onVisible = () => { if (!document.hidden) pull(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [everyMs]);
}

export function getItem(id: string): MediaItem | undefined {
  return items.find((it) => it.id === id);
}

/**
 * What a given viewer is allowed to see, newest first.
 *
 * A hidden photo is visible to an admin **or to the person who uploaded it**.
 * The second half was missing, and the API had it right all along: `getById`
 * already serves a hidden item to its owner, so the client was hiding
 * something the server would happily have returned by direct link. Hiding is a
 * moderation tool (D40) — it takes a photo off the shared wall, it does not
 * confiscate it from the guest who took it.
 */
export function visibleTo(all: MediaItem[], viewer: { userId: string; isAdmin: boolean } | null) {
  return all
    .filter((it) => (
      !it.hidden || Boolean(viewer?.isAdmin) || it.uploaderId === viewer?.userId
    ))
    .filter((it) => it.state !== 'rejected')
    .sort((a, b) => b.createdAt - a.createdAt);
}

function patch(id: string, changes: Partial<MediaItem>) {
  items = items.map((it) => (it.id === id ? { ...it, ...changes } : it));
  emit();
}

/* Mutations are optimistic: the local row changes at once and the request
   follows. A guest tapping hide on a photo that is on a projector in the next
   room should see it go immediately, and a failure is recoverable by trying
   again — whereas a spinner on every caption keystroke is not. */

export function setCaption(id: string, caption: string) {
  const next = caption.trim() || undefined;
  patch(id, { caption: next });
  void api(`/photos/${id}`, { method: 'PATCH', json: { caption: next ?? '' } })
    .catch(() => { void refreshMedia().catch(() => {}); });
}

export function setHidden(id: string, hidden: boolean) {
  patch(id, { hidden });
  void api(`/photos/${id}/visibility`, { method: 'POST', json: { hidden } })
    .catch(() => { void refreshMedia().catch(() => {}); });
}

/** Local only. Used by `cancel` and by the server-delete path below. */
function forget(id: string) {
  const item = items.find((it) => it.id === id);
  if (item?.src.startsWith('blob:')) URL.revokeObjectURL(item.src);
  if (item?.thumb.startsWith('blob:')) URL.revokeObjectURL(item.thumb);
  items = items.filter((it) => it.id !== id);
  emit();
}

export function remove(id: string) {
  const item = items.find((it) => it.id === id);
  forget(id);
  // A row that never reached the server has nothing to delete there.
  if (item?.state === 'done') {
    void api(`/photos/${id}`, { method: 'DELETE' })
      .catch(() => { void refreshMedia().catch(() => {}); });
  }
}

/** Cancel is not delete: it stops an upload that is still going and leaves the
 *  server alone, because nothing was ever written there. */
export function cancel(id: string) {
  aborters.get(id)?.abort();
  aborters.delete(id);
  forget(id);
}

/* ── uploading ─────────────────────────────────────────────────────────────*/

export class RejectedFile extends Error {
  constructor(public fileName: string, public reason: string) { super(reason); }
}

/** Reads duration from the file itself, before a byte is uploaded (D25). */
export function videoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(el.duration); };
    el.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unreadable')); };
    el.src = url;
  });
}

/** Long edge of the copy guests are actually served. The reception projector is
 *  1080p, so 2560 is a downscale on the way to the screen with room to spare —
 *  the pixels below this are the ones nobody can see (D25 amendment). */
const DISPLAY_LONG_EDGE = 2560;
const THUMB_LONG_EDGE = 480;

/**
 * Encode an already-decoded bitmap down to `longEdge`.
 *
 * Split out from the decode deliberately. Decoding a 24-megapixel phone photo
 * is the expensive half and it used to happen once per output size — the
 * display copy and the thumbnail each decoded the same file from scratch.
 * Decode once, encode twice.
 *
 * `OffscreenCanvas.convertToBlob` keeps the encode off the main thread where
 * the browser has it (Chrome, Safari 16.4+). `canvas.toBlob` is the fallback
 * and janks; that is the price of an old browser, not of every browser.
 */
async function encodeFrom(bitmap: ImageBitmap, longEdge: number, quality: number): Promise<Blob | null> {
  // Never upscale. A source already inside the bound is left exactly as it is —
  // inflating it adds bytes and no detail.
  const scale = Math.min(1, longEdge / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));

  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, w, h);
    try {
      return await canvas.convertToBlob({ type: 'image/webp', quality });
    } catch {
      return null;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0, w, h);
  return await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/webp', quality);
  });
}

/**
 * Decode a file to a bitmap, or null if the browser cannot read it — desktop
 * Chrome cannot decode HEIC, and an iPhone photo AirDropped to a laptop arrives
 * as one.
 *
 * Null means "use the original untouched", which is never worse than what we
 * would have sent, only slower. It must never mean "fail the upload".
 */
async function decode(file: Blob): Promise<ImageBitmap | null> {
  try {
    return await createImageBitmap(file);
  } catch {
    return null;
  }
}

/**
 * The two artefacts an upload actually produces, both made here rather than
 * server-side (D25 amendment).
 *
 * `display` is what the gallery, photo detail and the projector are served.
 * `thumb` is the grid tile. The full-resolution original is not uploaded: at
 * ~4 MB it is sixteen seconds of hotel wifi per photo against four, and the
 * detail it carries is below what a 1080p screen can resolve.
 */
export async function derivativesFor(file: File): Promise<{ display: Blob; thumb: Blob | null }> {
  if (!file.type.startsWith('image/')) return { display: file, thumb: null };
  const bitmap = await decode(file);
  if (!bitmap) return { display: file, thumb: null };
  try {
    const alreadySmall = Math.max(bitmap.width, bitmap.height) <= DISPLAY_LONG_EDGE;
    const display = alreadySmall ? null : await encodeFrom(bitmap, DISPLAY_LONG_EDGE, 0.82);
    const thumb = await encodeFrom(bitmap, THUMB_LONG_EDGE, 0.7);
    return { display: display ?? file, thumb };
  } finally {
    bitmap.close();
  }
}

/**
 * A video's poster frame — both as an object URL for the tile and as a blob to
 * upload, because a video has no display copy until MediaConvert has run and
 * the poster is the only thing the gallery can show until then.
 *
 * Photos get theirs from `derivativesFor`, which has already decoded the file
 * and can encode both sizes from the one bitmap.
 */
interface Poster {
  url: string;
  blob: Blob | null;
  /** Source pixel dimensions of the clip. Sent on to the presigner, which
   *  signs them into the upload as S3 metadata so the transcoder can size its
   *  output without upscaling — see `media-processor`'s `outputSize`. This is
   *  the only place in the browser that ever learns them. */
  width?: number;
  height?: number;
}

async function posterFrame(url: string): Promise<Poster> {
  return new Promise((resolve) => {
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.muted = true;
    el.src = url;
    const nothing: Poster = { url: '', blob: null };

    el.onloadeddata = () => {
      try {
        el.currentTime = Math.min(0.5, el.duration / 2);
      } catch { resolve(nothing); }
    };
    el.onseeked = () => {
      const vw = el.videoWidth;
      const vh = el.videoHeight;
      if (!vw || !vh) return resolve(nothing);

      // Scale the LONG edge to 480, not the width. Pinning width meant a
      // portrait clip — which is most of them — produced a 480x853 poster:
      // nearly twice the pixels asked for, on the thumbnail, which is the
      // single largest bandwidth cost in the system.
      const scale = Math.min(1, THUMB_LONG_EDGE_PX / Math.max(vw, vh));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(vw * scale);
      canvas.height = Math.round(vh * scale);

      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve(nothing);
      ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
      // WebP to match what the thumb target's presign policy expects.
      canvas.toBlob(
        (blob) => resolve(blob
          ? { url: URL.createObjectURL(blob), blob, width: vw, height: vh }
          : nothing),
        'image/webp',
        0.7,
      );
    };
    el.onerror = () => resolve(nothing);
  });
}

export interface Uploader { userId: string; name: string; team: Suit }

/**
 * Adds files to the queue. Anything unacceptable is turned away here, with a
 * sentence, rather than after a three-minute wait.
 */
export async function enqueue(files: File[], uploader: Uploader, caption?: string, eventId?: string) {
  const posterWork: (() => Promise<void>)[] = [];

  for (const file of files) {
    const id = `up-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    if (!isVideo && !isImage) {
      items = [{
        id, kind: 'photo', src: '', thumb: '', uploaderId: uploader.userId,
        uploaderName: uploader.name, uploaderTeam: uploader.team, createdAt: now(),
        hidden: false, state: 'rejected', fileName: file.name,
        error: 'That is not a photo or a video, so there is nothing to add.',
      }, ...items];
      emit();
      continue;
    }

    const url = URL.createObjectURL(file);
    let durationSec: number | undefined;

    if (isVideo) {
      try {
        durationSec = await videoDuration(file);
      } catch {
        durationSec = undefined;
      }
      if (durationSec && durationSec > MAX_VIDEO_SECONDS) {
        URL.revokeObjectURL(url);
        items = [{
          id, kind: 'video', src: '', thumb: '', durationSec,
          uploaderId: uploader.userId, uploaderName: uploader.name, uploaderTeam: uploader.team,
          createdAt: now(), hidden: false, state: 'rejected', fileName: file.name,
          error: `That one is ${formatDuration(durationSec)} long. Videos have to be under three minutes — nothing was uploaded.`,
        }, ...items];
        emit();
        continue;
      }
    }

    // The row appears NOW, with no poster. Generating it first meant a decode
    // of every earlier file had to finish before the tenth even showed up in
    // the queue — pick twenty photos and the last one was invisible for the
    // best part of a minute, which reads exactly like a dropped upload.
    items = [{
      id, kind: isVideo ? 'video' : 'photo', src: url, thumb: '', durationSec,
      uploaderId: uploader.userId, uploaderName: uploader.name, uploaderTeam: uploader.team,
      caption, eventId, createdAt: now(), hidden: false,
      state: 'queued', progress: 0, fileName: file.name,
    }, ...items];
    emit();

    posterWork.push(async () => {
      if (isVideo) {
        const poster = await posterFrame(url);
        if (!items.some((it) => it.id === id)) return;
        if (poster.url) patch(id, { thumb: poster.url });
        // The clip itself is what goes up; there is no browser-side transcode.
        // Above the multipart threshold `run` takes the resumable path.
        pending.set(id, {
          kind: 'video', contentType: file.type, primary: file,
          thumb: poster.blob, durationSec, caption, eventId,
          width: poster.width, height: poster.height,
        });
        void run(id);
        return;
      }

      // Derivatives before anything is sent. The camera original never leaves
      // the phone (D25): on this photo set that is a 21 MB file becoming a
      // 0.8 MB upload — twenty-eight times less to push over hotel wifi.
      const { display, thumb } = await derivativesFor(file);
      if (!items.some((it) => it.id === id)) return;
      patch(id, { thumb: thumb ? URL.createObjectURL(thumb) : url });
      pending.set(id, {
        kind: 'photo',
        // What is actually being sent — WebP normally, the untouched file when
        // the browser could not decode it (HEIC on desktop Chrome). Telling
        // the presigner the wrong type fails the POST policy's Content-Type
        // condition, not the transfer, so it must match the blob exactly.
        contentType: display === file ? file.type : 'image/webp',
        primary: display,
        thumb,
        caption,
        eventId,
      });
      void run(id);
    });
  }

  await pooled(posterWork, MAX_CONCURRENT_DECODES);
}

/**
 * Run tasks `limit` at a time. Decoding is CPU-bound, so more workers than this
 * does not go faster — it just competes with the render thread for the same
 * cores and makes the queue stutter while it scrolls.
 */
async function pooled(tasks: (() => Promise<void>)[], limit: number): Promise<void> {
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    for (let i = next++; i < tasks.length; i = next++) await tasks[i]!();
  });
  await Promise.all(workers);
}

const running = new Set<string>();

/** Live uploads, so cancel can actually stop one mid-flight rather than just
 *  hiding the row while the bytes keep going. */
const aborters = new Map<string, AbortController>();

/**
 * How many uploads are in flight at once.
 *
 * On a bandwidth-bound link this does not make the total faster — measured, 20
 * files take the same wall-clock at 1, 3 or 6. What it buys is the per-file
 * overhead: a presign round-trip and a TLS handshake each, most of a second on
 * a resort connection, previously all in series. And one stalled transfer no
 * longer holds up the nineteen behind it.
 */
const MAX_CONCURRENT_TRANSFERS = 3;

/** Decodes are CPU-bound; a separate budget from the network one. */
const MAX_CONCURRENT_DECODES = 2;

let activeTransfers = 0;
const waitingForSlot: (() => void)[] = [];

function acquireSlot(): Promise<void> {
  if (activeTransfers < MAX_CONCURRENT_TRANSFERS) {
    activeTransfers += 1;
    return Promise.resolve();
  }
  // Queued. The releasing transfer hands its slot straight over rather than
  // decrementing, so the count cannot drift.
  return new Promise<void>((resolve) => waitingForSlot.push(resolve));
}

function releaseSlot(): void {
  const next = waitingForSlot.shift();
  if (next) next();
  else activeTransfers -= 1;
}

interface Pending {
  kind: MediaKind;
  contentType: string;
  primary: Blob;
  thumb: Blob | null;
  durationSec?: number;
  /** Video only. Source dimensions, on their way to becoming S3 metadata. */
  width?: number;
  height?: number;
  caption?: string;
  eventId?: string;
}

/** What each queued row still needs in order to be sent, kept out of the item
 *  itself so blobs are not handed to every component that renders a tile. */
const pending = new Map<string, Pending>();

async function run(localId: string) {
  if (running.has(localId)) return;
  running.add(localId);

  await acquireSlot();

  const work = pending.get(localId);
  if (!work || !items.some((it) => it.id === localId)) {
    running.delete(localId);
    releaseSlot();
    return;
  }

  const controller = new AbortController();
  aborters.set(localId, controller);
  patch(localId, { state: 'uploading', progress: 0, error: undefined });

  try {
    const onProgress = (f: number) => patch(localId, { progress: f });

    const photoId = needsMultipart(work.primary.size)
      ? await uploadMultipart({
        file: work.primary,
        contentType: work.contentType,
        durationSec: work.durationSec,
        width: work.width,
        height: work.height,
        thumb: work.thumb,
        onProgress,
        signal: controller.signal,
      })
      : await uploadSingle({
        kind: work.kind,
        contentType: work.contentType,
        primary: work.primary,
        thumb: work.thumb,
        durationSec: work.durationSec,
        width: work.width,
        height: work.height,
        onProgress,
        signal: controller.signal,
      });

    // The bytes are in S3 but the row does not exist until this returns —
    // deliberately, so an upload that dies halfway leaves no half-photo in the
    // gallery for anyone to explain.
    patch(localId, { state: 'processing', progress: 1 });
    await api('/photos/complete', {
      method: 'POST',
      json: {
        photoId,
        kind: work.kind,
        durationSec: work.durationSec,
        caption: work.caption,
        eventId: work.eventId,
      },
    });

    patch(localId, { state: 'done', progress: 1 });
    pending.delete(localId);

    // Pick up the server's own copy — its id, its attribution, and the URL the
    // gallery will actually serve rather than this browser's blob.
    void refreshMedia().catch(() => {});
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      // Cancelled. `cancel` has already removed the row.
    } else if (err instanceof NotSignedIn) {
      patch(localId, { state: 'failed', error: 'Your sign-in ran out. Sign in again and this will go up.' });
    } else if (!navigator.onLine) {
      patch(localId, { state: 'failed', error: "The wifi dropped out. Try again when it's back." });
    } else {
      patch(localId, {
        state: 'failed',
        error: err instanceof ApiError ? err.message : 'That did not go up. Try again.',
      });
    }
  } finally {
    aborters.delete(localId);
    releaseSlot();
    running.delete(localId);
  }
}

export function retry(id: string) {
  if (!pending.has(id)) return;
  patch(id, { state: 'queued', progress: 0, error: undefined });
  void run(id);
}


/** Anything the wifi dropped picks itself back up (principle 5: be trustworthy). */
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    items.filter((it) => it.state === 'failed').forEach((it) => retry(it.id));
  });
}

export function formatDuration(seconds: number): string {
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function estimateLabel(item: MediaItem, bytesPerSecond = 260_000): string {
  if (item.state !== 'uploading' || item.progress === undefined) return '';
  const remaining = (1 - item.progress) * (1_600_000 / bytesPerSecond);
  if (remaining < 5) return 'a few seconds left';
  if (remaining < 90) return `${Math.round(remaining)} seconds left`;
  return `about ${Math.round(remaining / 60)} minutes left`;
}
